import { nanoid } from '@reduxjs/toolkit';
import { DOCUMENT_TITLE_TYPE, SOURCE } from '../data/constants';
import { AUTH_TOKEN, SESSION_ID, STATE } from '../data/local-storage-constants';
import {
  clearLocalStorage,
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '../utils';
import { isFlipkartFlow } from '../features/credit-report-apply/helper';
import history from '../history';
import { GST_ROUTE, HOME_PAGE_ROUTE } from '../data/routes-constants';
import {
  clearPollingData,
  logout,
  resetCreditReportData,
  resetLeads,
  resetLendersData,
} from '../redux/slices';

export const setDocumentTitle = (titleText: string) => {
  document.title = titleText;
  return () => {
    document.title = DOCUMENT_TITLE_TYPE.default;
  };
};

export const formatDate = (dateString: string, formatType: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const getUTMValue = (key: string) => {
  return sessionStorage.getItem(key) || '';
};

export const appendQueryParamsToHref = () => {
  const hostname = window.location.hostname;
  // Check if running on localhost or a local network IP (e.g., 192.168.x.x or 10.x.x.x)
  const isLocal =
    hostname === 'localhost' ||
    /^192\.168\.\d+\.\d+$/.test(hostname) ||
    /^10\.\d+\.\d+\.\d+$/.test(hostname);
  if (isLocal) {
    return 'www.testFromLocal.com';
  }
  const utmSource = sessionStorage.getItem(SOURCE.utm_source) || '';
  const utmMedium = sessionStorage.getItem(SOURCE.utm_medium) || '';
  const utmCampaign = sessionStorage.getItem(SOURCE.utm_campaign) || '';
  const utmFkSellerId = sessionStorage.getItem(SOURCE.fk_seller_id) || '';

  if (!utmSource && !utmMedium && !utmCampaign) {
    return window.location.href; // No UTM parameters to append
  }

  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  if (!params.has(SOURCE.utm_source) && utmSource) {
    params.append(SOURCE.utm_source, utmSource);
  }

  if (!params.has(SOURCE.utm_medium) && utmMedium) {
    params.append(SOURCE.utm_medium, utmMedium);
  }

  if (!params.has(SOURCE.utm_campaign) && utmCampaign) {
    params.append(SOURCE.utm_campaign, utmCampaign);
  }

  if (!params.has(SOURCE.fk_seller_id) && utmFkSellerId) {
    params.append(SOURCE.fk_seller_id, utmFkSellerId);
  }

  const newUrl = `${url.origin}${url.pathname}?${params.toString()}`;
  return newUrl;
};

export const checkIfEnhancedFlow = () => {
  const utmSource = sessionStorage.getItem(SOURCE.utm_source) || '';
  const utmMedium = sessionStorage.getItem(SOURCE.utm_medium) || '';
  if (
    utmSource &&
    utmMedium &&
    utmMedium === 'ds' &&
    utmSource === 'internal-partnership'
  ) {
    return true;
  }
  return false;
};

export const getLoanTypeQueryParam = () => {
  const value = getQueryParam('loanType');
  if (value && value.includes('?')) {
    return value.split('?')[0] || '';
  }
  return value;
};

export const getQueryParam = (key: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(key) || '';
};

export function extractFirstNumber(input: string | number | null | undefined) {
  // Convert the input to a string
  const str = typeof input === 'string' ? input : input?.toString();

  if (!str) {
    return null; // Return null if the input is undefined, null, or cannot be converted to a string
  }

  // Check if the string is purely numeric (consisting only of digits)
  if (/^\d+$/.test(str)) {
    return str; // Return the number as it is
  }

  // Otherwise, extract the first number from the string
  const match = str && str.match(/\d+/);
  let number = match ? parseInt(match[0], 10) : null; // Convert to integer

  // If 'Cr' is present AND the number is directly associated with it (after the number), multiply by 100
  if (number && /Cr/i.test(str) && str.includes(`${number} Cr`)) {
    number *= 100;
  }

  return number;
}

export interface LeadItem {
  leadId: string;
  landingUrl: string;
  name: string;
  productType: string;
  lastInterestAt: string;
  extraInfo?: string; // optional, since it may not exist in all objects
}

export const isLeadPresent = (leadArr: LeadItem[], loanType: string) => {
  let matched = false;
  leadArr.forEach((item) => {
    if (item.productType === loanType) {
      matched = true;
    }
  });
  return matched;
};

export const manageSession = () => {
  const current_session = getLocalStorage(SESSION_ID);
  if (!current_session) {
    setLocalStorage(SESSION_ID, nanoid(15));
  }
};

export const webviewParentDevice = (): 'android' | 'ios' | 'desktop' | '' => {
  const windowObj = window as any;

  if (windowObj?.Android) {
    return 'android';
  } else if (windowObj?.ReactNativeWebView) {
    return 'ios';
  } else if (windowObj?.parent?.postMessage) {
    return 'desktop';
  } else {
    return '';
  }
};

export const triggerAppEvent = (eventData: any) => {
  const device = webviewParentDevice();
  const windowObj = window as any;
  let json: string | null = null;
  try {
    json = JSON.stringify(eventData);
  } catch (err) {
    json = null;
  }
  if (windowObj?.Android) {
    windowObj.Android.sendData(json);
  } else {
    if (windowObj?.ReactNativeWebView) {
      windowObj.ReactNativeWebView.postMessage(JSON.stringify(eventData));
    }
  }
  if (device === 'desktop') {
    windowObj.parent.postMessage(
      eventData,
      process.env.NEXT_PUBLIC_DIGITAL_SHOWROOM_WEB_URL
    );
  }
};

export function compareVersions(version1: string, version2: string) {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);

  const maxLength = Math.max(v1Parts.length, v2Parts.length);

  for (let i = 0; i < maxLength; i++) {
    const v1 = v1Parts[i] !== undefined ? v1Parts[i] : 0;
    const v2 = v2Parts[i] !== undefined ? v2Parts[i] : 0;

    if (v1 > v2) {
      return 1; // version1 is greater than version2
    }
    if (v1 < v2) {
      return -1; // version1 is less than version2
    }
  }

  return 0; // versions are equal
}

export function generateUniqueID() {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

export const handleLogout = (dispatch: any) => {
  isFlipkartFlow() ? history.push(GST_ROUTE) : history.push(HOME_PAGE_ROUTE);
  removeLocalStorage(AUTH_TOKEN);
  removeLocalStorage(STATE);
  clearLocalStorage();
  dispatch(logout());
  dispatch(resetCreditReportData());
  dispatch(resetLeads());
  dispatch(resetLendersData());
  dispatch(clearPollingData());
};
